import { useRouter } from 'next/compat/router';
import { useCallback, useEffect, useMemo } from 'react';

type Url =
	| string
	| {
			pathname?: string;
			query?: Record<string, string | string[] | undefined>;
	  };
type PushOptions = {
	shallow?: boolean;
	locale?: string | false;
	scroll?: boolean;
};

type ReplaceOptions = {
	shallow?: boolean;
	locale?: string | false;
	scroll?: boolean;
};

export const useBrowsing = () => {
	const router = useRouter();
	useEffect(() => {
		if (!router?.isReady) return;
	}, [router?.isReady]);

	const pathnameSafe =
		router?.pathname ??
		(typeof window !== 'undefined' ? window.location.pathname : '');
	const querySafe =
		router?.query ??
		(typeof window !== 'undefined'
			? Object.fromEntries(new URLSearchParams(window.location.search))
			: {});
	const asPathSafe =
		router?.asPath ??
		(typeof window !== 'undefined'
			? `${window.location.pathname}${window.location.search}`
			: '');
	const basePathSafe = router?.basePath ?? '';
	const eventsSafe = router?.events;
	const isReadySafe = router?.isReady ?? false;

	const push = useCallback(
		(url: Url, as?: Url, options?: PushOptions) => {
			if (!router?.push) return Promise.resolve(false);
			return router.push(url, as, options);
		},
		[router]
	);

	const replace = useCallback(
		(url: Url, as?: Url, options?: ReplaceOptions) => {
			if (!router?.replace) return Promise.resolve(false);
			return router.replace(url, as, options);
		},
		[router]
	);

	return useMemo(
		() => ({
			pathname: pathnameSafe,
			query: querySafe,
			asPath: asPathSafe,
			basePath: basePathSafe,
			events: eventsSafe,
			isReady: isReadySafe,
			push,
			replace,
		}),
		[
			pathnameSafe,
			querySafe,
			asPathSafe,
			basePathSafe,
			eventsSafe,
			isReadySafe,
			push,
			replace,
		]
	);
};

export default useBrowsing;
